import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useAllCardsStore,
  useGetCard3DSStore,
  useAllInvoicesStore,
  useAllStatementsStore,
  useInvoicesStore,
  useViewStatementsStore,
  useViewInvoicesStore,
} from '@lib/store';
import { useMediaQuery, useRouteHook } from '@shared/hooks';
import { cn, LocalStorageUtils, RoutePaths } from '@shared/utils';
import type { GetAllCardsResponse } from '@shared/api';
import {
  Option,
  ChangeNameYourCardModal,
  SuccessfullyChangeNameYourCardModal,
  ChangeYourPinModal,
  SuccessfullyChangeYourPinModal,
  BlockUnblockModal,
  SuccessfullyBlockUnblockModal,
  LostOrStolenModal,
  SuccessfullyLostOrStolenModal,
  Update3dSecureNumberModal,
  SuccessfullyUpdate3dSecureNumberModal,
  Card,
  AddMoney,
} from './widgets';
import { CardSlider, PdfViewer } from '@widgets';
import { Button, PageLayout, Preloader } from '@shared/widgets';
import ArrowMobileIcon from '@assets/images/arrow_mobile.svg';
import NameIcon from '@assets/images/cards/name_ico.svg';
import PinIcon from '@assets/images/cards/pin_ico.svg';
import BlockIcon from '@assets/images/cards/block_ico.svg';
import StolenIcon from '@assets/images/cards/stolen_ico.svg';
import SecurityIcon from '@assets/images/cards/security.svg';
import SecureIcon from '@assets/images/cards/secure.svg';
import ArrowIcon from '@assets/images/arrow.svg';
import WriteIcon from '@assets/images/ManageCards/write.png';
import AttentionIcon from '@assets/images/ManageCards/attention.png';
import KeyIcon from '@assets/images/ManageCards/key.png';
import LockIcon from '@assets/images/ManageCards/lock.png';

export const ManageCardsPage = () => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const allInvoicesStore = useAllInvoicesStore();
  const allStatementsStore = useAllStatementsStore();
  const invoicesStore = useInvoicesStore();
  const allCardsStore = useAllCardsStore();
  const viewStatementsStore = useViewStatementsStore();
  const viewInvoicesStore = useViewInvoicesStore();
  const getCard3dsStore = useGetCard3DSStore();

  const isMaxMd = useMediaQuery('(max-width: 768px)');

  const [selectedCard, setSelectedCard] = useState<GetAllCardsResponse>();
  const [shownCard, setShownCard] = useState<number | null>(null);
  const [addMoneyCard, setAddMoneyCard] = useState<GetAllCardsResponse | null>(null);
  const [cardOptions, setCardOptions] = useState<boolean>(false);
  // MODALS
  const [showNameYourCardModal, setShowNameYourCardModal] = useState<boolean>(false);
  const [showSuccessfullyChangeNameModal, setShowSuccessfullyChangeNameModal] = useState<boolean>(false);
  const [showChangeYourPINModal, setShowChangeYourPINModal] = useState<boolean>(false);
  const [showSuccessfullyChangeYourPINModal, setShowSuccessfullyShowChangeYourPINModal] = useState<boolean>(false);
  const [showUnblockModal, setShowUnblockModal] = useState<boolean>(false);
  const [showSuccessfullyUnblockModal, setShowSuccessfullyUnblockModal] = useState<boolean>(false);
  const [showLostOrStolen, setShowLostOrStolen] = useState<boolean>(false);
  const [showSuccessfullyLostOrStolenModal, setShowSuccessfullyLostOrStolenModal] = useState<boolean>(false);
  const [showUpdate3dsSecureNumberModal, setShowUpdate3dsSecureNumberModal] = useState<boolean>(false);
  const [showSuccessfullyUpdate3dsSecureNumberModal, setShowSuccessfullyUpdate3dsSecureNumberModal] = useState<boolean>(false);
  const [initialSlide, setInitialSlide] = useState<number>(0);

  useEffect(() => {
    if (showUpdate3dsSecureNumberModal) getCard3dsStore.get(selectedCard!.iclCardId);
  }, [showUpdate3dsSecureNumberModal, selectedCard?.iclCardId]);

  useEffect(() => {
    const card = LocalStorageUtils.getSelectedCard();
    if (card) setSelectedCard(card);
    if (selectedCard) invoicesStore.get(selectedCard.productId || 1);
  }, []);

  useEffect(() => {
    if (allCardsStore.data && allCardsStore.data.length) {
      if (selectedCard) {
        const found = allCardsStore.data.filter((item) => item.id === selectedCard.id)[0];
        if (!found) setSelectedCard(allCardsStore.data[0]);
      } else setSelectedCard(allCardsStore.data[0]);
    }
  }, [allCardsStore.data]);

  useEffect(() => {
    allCardsStore.clear();
    allCardsStore.get();

    if (location?.state) {
      setSelectedCard(location.state.item);
      setShownCard(location.state.index);
      setInitialSlide(location.state.index);
      if (location.state?.item?.id) allStatementsStore.getAllStatementsCard(location.state?.item?.id);
    }

    if (location?.state?.cardOptions) setCardOptions(true);
  }, []);

  useEffect(() => {
    if (location.pathname === '/') setCardOptions(false);
  }, [location.pathname]);

  const viewStatementHandler = () => {
    if (viewStatementsStore.loading) return;
    viewStatementsStore.viewStatement(allStatementsStore.data[0].id);
    setSelectedCard(selectedCard);
    setShownCard(shownCard);
  };
  const viewInvoiceHandler = () => {
    if (viewInvoicesStore.loading) return;
    viewInvoicesStore.viewInvoice(allInvoicesStore.data[0].id);
    setSelectedCard(selectedCard);
    setShownCard(shownCard);
  };

  const handleManageCards = () => {
    navigate(RoutePaths.manageCards.onNavigate(), { state: { item: selectedCard, index: shownCard, cardOptions: true } });
    setCardOptions(true);
  };

  useEffect(() => {
    if (allCardsStore.data && selectedCard) {
      const updatedCard = allCardsStore.data.find((card) => card.id === selectedCard.id);
      if (updatedCard) setSelectedCard(updatedCard);
    }
  }, [allCardsStore.data]);

  const handleCardSelect = (card: GetAllCardsResponse, index: number) => {
    LocalStorageUtils.setSelectedCardIndex(index!);
    LocalStorageUtils.setSelectedCard(card!);
    setSelectedCard(card);
    setShownCard(index!);
  };

  const renderCurrentCard = () => {
    return (
      <div className='px-4 pb-12'>
        {selectedCard && <Card card={selectedCard} />}
        <ul className='py-8'>
          <li
            className='relative mb-3 flex h-15 w-full items-center justify-between bg-white px-4 font-[Prata] text-lg text-[#0f0f11]'
            onClick={() => (!isMaxMd ? navigate(RoutePaths.home.onNavigate()) : navigate(RoutePaths.cards.onNavigate(), { state: { selectedCard } }))}
          >
            <div className='flex items-center gap-2'>
              <NameIcon />
              Back
            </div>
            <ArrowMobileIcon />
          </li>
          <li
            className='relative mb-3 flex h-15 w-full items-center justify-between bg-white px-4 font-[Prata] text-lg text-[#0f0f11]'
            onClick={() => setShowNameYourCardModal(true)}
          >
            <div className='flex items-center gap-2'>
              <NameIcon />
              Name Your Card
            </div>
            <ArrowMobileIcon />
          </li>
          <li
            className='relative mb-3 flex h-15 w-full items-center justify-between bg-white px-4 font-[Prata] text-lg text-[#0f0f11]'
            onClick={() => setShowChangeYourPINModal(true)}
          >
            <div className='flex items-center gap-2'>
              <PinIcon />
              Change Your PIN
            </div>
            <ArrowMobileIcon />
          </li>
          <li
            className='relative mb-3 flex h-15 w-full items-center justify-between bg-white px-4 font-[Prata] text-lg text-[#0f0f11]'
            onClick={() => setShowUnblockModal(true)}
          >
            <div className='flex items-center gap-2'>
              <BlockIcon />
              Block / Unblock
            </div>
            <ArrowMobileIcon />
          </li>
          <li
            className='relative mb-3 flex h-15 w-full items-center justify-between bg-white px-4 font-[Prata] text-lg text-[#0f0f11]'
            onClick={() => setShowLostOrStolen(true)}
          >
            <div className='flex items-center gap-2'>
              <StolenIcon />
              Lost or Stolen Card
            </div>
            <ArrowMobileIcon />
          </li>
          <li
            className='relative mb-3 flex h-15 w-full items-center justify-between bg-white px-4 font-[Prata] text-lg text-[#0f0f11]'
            onClick={() => setShowUpdate3dsSecureNumberModal(true)}
          >
            <div className='flex items-center gap-2'>
              <SecurityIcon />
              Update 3D Secure Number
            </div>
            <ArrowMobileIcon />
          </li>
        </ul>
      </div>
    );
  };

  const renderButtons = () => {
    if (selectedCard) {
      if (cardOptions) {
        return (
          <div>
            <Option
              icon={<ArrowIcon className='rotate-180' />}
              title='Back'
              description='Return to previous menu'
              onClick={() => navigate(RoutePaths.home.onNavigate())}
            />
            <Option
              icon={WriteIcon}
              title='Name Your Card'
              description='Create custom name'
              onClick={() => setShowNameYourCardModal(!showNameYourCardModal)}
            />
            <Option
              icon={KeyIcon}
              title='Change Your PIN'
              description='For security you will be issued a new card'
              onClick={() => setShowChangeYourPINModal(!showChangeYourPINModal)}
            />
            <Option
              icon={LockIcon}
              title='Block / Unblock'
              description='Temporarily or permanently block your card'
              onClick={() => setShowUnblockModal(!showUnblockModal)}
            />
            <Option
              icon={AttentionIcon}
              title='Lost or Stolen Card'
              description='Report your card lost or stolen'
              onClick={() => setShowLostOrStolen(!showLostOrStolen)}
            />
            <Option
              icon={<SecureIcon />}
              title='Update 3D Secure Number'
              description='Update 3D Secure Number'
              onClick={() => setShowUpdate3dsSecureNumberModal(true)}
            />
          </div>
        );
      } else {
        if (selectedCard) {
          return (
            <div className='flex flex-col gap-4'>
              {allInvoicesStore.data && allInvoicesStore.data[0] && <Button onClick={viewInvoiceHandler}>View Invoice</Button>}
              {allStatementsStore.data && allStatementsStore.data[0] && <Button onClick={viewStatementHandler}>View Statement</Button>}
              <Button onClick={handleManageCards}>Manage Card</Button>
            </div>
          );
        }
      }
    }
    return null;
  };

  return (
    <PageLayout>
      {allCardsStore.data ? (
        <div className='w-full'>
          <div className={cn('mx-auto flex max-w-360 flex-col py-23', 'max-md:hidden')}>
            <div className='mb-12.5 w-full'>
              <CardSlider
                className='mx-auto mt-25 max-w-360 pr-21.25 pl-11.25'
                onSelectCard={handleCardSelect}
                cards={allCardsStore.data}
                toggleCards
                showCardId={shownCard}
                dontRotate
                goToSlideNumber={initialSlide}
                renderButtons={renderButtons}
              />
            </div>
          </div>
          <div className={cn('hidden', 'max-md:inherit max-md:pt-7.5')}>
            <h2 className={cn('mt-27.75 w-full text-center font-[Prata] text-[56px] text-[#272727]', 'max-md:m-0 max-md:mb-4 max-md:text-[28px]')}>
              Manage the Card
            </h2>
            {renderCurrentCard()}
          </div>
          <AddMoney card={addMoneyCard} onClose={() => setAddMoneyCard(null)} />
          <ChangeNameYourCardModal
            isOpen={showNameYourCardModal}
            onClose={() => setShowNameYourCardModal(false)}
            card={selectedCard!}
            onConfirm={() => setShowSuccessfullyChangeNameModal(true)}
          />
          <SuccessfullyChangeNameYourCardModal isOpen={showSuccessfullyChangeNameModal} onClose={() => setShowSuccessfullyChangeNameModal(false)} />
          <ChangeYourPinModal
            isOpen={showChangeYourPINModal}
            onClose={() => setShowChangeYourPINModal(false)}
            card={selectedCard!}
            onConfirm={() => setShowSuccessfullyShowChangeYourPINModal(true)}
          />
          <SuccessfullyChangeYourPinModal
            isOpen={showSuccessfullyChangeYourPINModal}
            onClose={() => setShowSuccessfullyShowChangeYourPINModal(false)}
          />
          <BlockUnblockModal
            isOpen={showUnblockModal}
            onClose={() => setShowUnblockModal(false)}
            card={selectedCard!}
            onConfirm={() => setShowSuccessfullyUnblockModal(true)}
          />
          <SuccessfullyBlockUnblockModal isOpen={showSuccessfullyUnblockModal} onClose={() => setShowSuccessfullyUnblockModal(false)} />
          <LostOrStolenModal
            isOpen={showLostOrStolen}
            onClose={() => setShowLostOrStolen(false)}
            card={selectedCard!}
            onConfirm={() => setShowSuccessfullyLostOrStolenModal(true)}
          />
          <SuccessfullyLostOrStolenModal isOpen={showSuccessfullyLostOrStolenModal} onClose={() => setShowSuccessfullyLostOrStolenModal(false)} />
          <Update3dSecureNumberModal
            isOpen={showUpdate3dsSecureNumberModal}
            onClose={() => setShowUpdate3dsSecureNumberModal(false)}
            card={selectedCard!}
            onConfirm={() => setShowSuccessfullyUpdate3dsSecureNumberModal(true)}
          />
          <SuccessfullyUpdate3dSecureNumberModal
            isOpen={showSuccessfullyUpdate3dsSecureNumberModal}
            onClose={() => setShowSuccessfullyUpdate3dsSecureNumberModal(false)}
          />
          {location.pathname !== '/cards' && (
            <PdfViewer
              open={!!viewStatementsStore.data || !!viewInvoicesStore.data}
              onClose={() => {
                viewStatementsStore.clear();
                viewInvoicesStore.clear();
              }}
              title={viewStatementsStore.data ? 'Statement' : 'Invoice'}
              data={viewStatementsStore.data || viewInvoicesStore.data}
            />
          )}
        </div>
      ) : (
        <Preloader preloaderOpacity={1} />
      )}
    </PageLayout>
  );
};
